<script setup lang="ts">
import LeIconButton from "@dsk/components/IconButton/LeIconButton.vue";
import LeLabel from "@dsk/components/Label/LeLabel.vue";
import LeTooltip from "@dsk/components/LeTooltip.vue";
import LeCross from "@dsk/icons/LeCross.vue";
import LeMagnifyingGlass from "@dsk/icons/LeMagnifyingGlass.vue";
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/vue";
import { VueDiff } from "@ordbogen-npm/vue-diff";
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";

import LeLanguagePair from "@/components/LeLanguagePair.vue";
import LeSpinner from "@/components/LeSpinner.vue";
import LeKeyboards from "@/components/searchbar/LeKeyboards.vue";
import LeLanguageSelector from "@/components/searchbar/LeLanguageSelector.vue";
import LeLanguageSelectorButton from "@/components/searchbar/LeLanguageSelectorButton.vue";
import { useFreeSearchLabel, useResponsiveness } from "@/composables";
import { createConfig, vFocusOnType } from "@/directives";
import { MatomoHelper } from "@/helpers";
import { useSearchFilterStore } from "@/stores";
import type {
  SearchDirection,
  SearchFilter,
  SearchLanguage,
  Suggestion,
  SuggestionSelection,
  SuggestionType
} from "@/types";
import { MatomoEventAction, MatomoEventCategory } from "@/types";

interface Props {
  autofocus?: boolean;
  focusOnType?: boolean;
  freeSearchesLeft: number | null;
  isLoading?: boolean;
  value?: string;
  suggestions: Suggestion[];
}

const props = withDefaults(defineProps<Props>(), {
  autofocus: false,
  focusOnType: false,
  freeSearchesLeft: null,
  isLoading: false,
  value: "",
  suggestions: () => []
});

const emit = defineEmits<{
  search: [data: { query: string; filter: SearchFilter }];
  change: [query: string];
  logSelection: [selection: SuggestionSelection];
}>();

const toast = useToast();
const { t } = useI18n();
const { isSmallerThanTabletScreen } = useResponsiveness();
const route = useRoute();

const { showFreeSearchCountdown, freeSearchLabelColor } = useFreeSearchLabel();
const searchFilterStore = useSearchFilterStore();

const comboboxInput = ref<typeof ComboboxInput>();
const isFocused = ref(false);
const query = ref(props.value);
const activeSuggestion = ref<Suggestion | undefined>(undefined);
const isSuggestionsOpen = ref<boolean>(false);

const focusOnTypeConfig = computed(() =>
  createConfig({
    active: props.focusOnType,
    ignoreKeys: [" "]
  })
);

const filterIsActive = computed<boolean>(() => Boolean(searchFilterStore.filter.sourceLanguage));

const isSmartComplete = (suggestion: Suggestion) => ["smart", "phrase"].includes(suggestion.type);

const showLanguageSelector = ref(false);
onMounted(() => {
  // If language selection is restored by session storage, and we are not on an article page, expand the language selector by default
  if (route.meta.isSearchRoute) {
    return;
  }

  if (searchFilterStore.filter.sourceLanguage) {
    showLanguageSelector.value = true;
  }
});

let shouldShowSuggestions = true;

const onLanguageSelectorButtonClick = () => {
  showLanguageSelector.value = !showLanguageSelector.value;
};

const onSourceLanguageChange = (language: SearchLanguage | undefined) => {
  searchFilterStore.filter.sourceLanguage = language;
};

const onDirectionChange = (direction: SearchDirection) => {
  searchFilterStore.filter.direction = direction;
};

const onTargetLanguageChange = (language: SearchLanguage | undefined) => {
  searchFilterStore.filter.targetLanguage = language;
};

const onClearQuery = () => {
  updateQuery("");
  setSearchBarFocus();
};

const onSearch = (value: string) => {
  if (!value.trim()) {
    return;
  }

  comboboxInput.value?.el.blur();

  // Send log to "queue", if the user selected a suggestion.
  if (activeSuggestion.value) {
    const suggestionIndex = props.suggestions.findIndex((s) => s.query === value);
    emit("logSelection", {
      uuid: activeSuggestion.value.uuid,
      selectedSuggestion: activeSuggestion.value.query,
      index: suggestionIndex,
      engine: activeSuggestion.value.type as SuggestionType
    });
  }

  query.value = value;
  activeSuggestion.value = undefined;
  showLanguageSelector.value = false;

  toast.clear();
  emit("search", { query: value, filter: searchFilterStore.filter });
};

const trackIconSearch = () => {
  MatomoHelper.sendEvent(MatomoEventCategory.Search, MatomoEventAction.ClickSearchIcon, "Searchbar icon was clicked");
};

const trackEnterSearch = () => {
  MatomoHelper.sendEvent(MatomoEventCategory.Search, MatomoEventAction.HitEnterToSearch, 'Searched by hitting "Enter"');
};

const trackSuggestionClick = () => {
  MatomoHelper.sendEvent(
    MatomoEventCategory.Search,
    MatomoEventAction.PickSuggestion,
    "Used wordcompletion or suggestions in dropdown"
  );
};

const onShowSuggestions = () => {
  if (props.suggestions?.length > 0) {
    return;
  }

  // Trigger change, that will fetch suggestions.
  emit("change", query.value);
};

const updateQuery = (suggestion: string | Suggestion) => {
  // If it's a string, we know they didn't select a suggestion option.
  if (typeof suggestion === "string") {
    query.value = suggestion;

    activeSuggestion.value = undefined;
    return;
  }

  shouldShowSuggestions = false;

  query.value = suggestion.query;
  activeSuggestion.value = suggestion;
};

watch(
  () => props.value,
  (value) => {
    query.value = value;

    shouldShowSuggestions = true;
  }
);

watch(query, (q) => {
  if (shouldShowSuggestions) {
    emit("change", q);
  }
});

const languagePairText = computed<string | null>(() => {
  const textPrefix = t("le-search-bar.language-selector.selected-language");

  if (searchFilterStore.filter.sourceLanguage && !searchFilterStore.filter.targetLanguage) {
    return `${textPrefix}: ${searchFilterStore.filter.sourceLanguage.name}`;
  }

  if (searchFilterStore.filter.sourceLanguage && searchFilterStore.filter.targetLanguage) {
    const text = `${textPrefix}: ${searchFilterStore.filter.sourceLanguage.name} ${t("le-search-bar.language-selector.to")} ${
      searchFilterStore.filter.targetLanguage.name
    }`;

    if (searchFilterStore.filter.direction == "one-way") {
      return text;
    }

    return `${text}, ${t("le-search-bar.language-selector.two-way")}`;
  }

  return null;
});

const languagePairAria = computed<string>(() => {
  const textPrefix = t("le-search-bar.language-selector.button-aria-label");

  if (languagePairText.value) {
    return `${textPrefix}. ${languagePairText.value}`;
  }

  return textPrefix;
});

const languageSelectorButtonAria = computed<string>(() => {
  const textPrefix = showLanguageSelector.value
    ? t("le-search-bar.language-selector.button-close-aria-label")
    : t("le-search-bar.language-selector.button-open-aria-label");

  if (isSmallerThanTabletScreen.value && languagePairText.value) {
    return `${textPrefix}. ${languagePairText.value}`;
  }

  return textPrefix;
});

const keyboardsEl = useTemplateRef("keyboards");

const onKeyboardKey = (key: string) => {
  isSuggestionsOpen.value = true;
  query.value = query.value.concat(key);
};

const onKeyboardBackspace = () => {
  isSuggestionsOpen.value = true;
  query.value = query.value.slice(0, -1);
};

const setSearchBarFocus = () => {
  comboboxInput.value?.el.focus();
};

const setFocus = (event: FocusEvent, focus: boolean) => {
  isFocused.value = focus;

  if (isFocused.value) {
    // Show word completion suggestions
    onShowSuggestions();
  }

  // Keep suggestions open if the user tabs from input to the keyboard toggle button
  if (isFocused.value === false && (event.relatedTarget as Element | null)?.id === keyboardsEl.value?.toggleButtonId) {
    return;
  }

  if (
    isFocused.value === false &&
    (event.relatedTarget as Element | null)?.className ===
      "le-search-bar__suggestions-item le-search-bar__suggestions-item--active"
  ) {
    return;
  }

  isSuggestionsOpen.value = isFocused.value;
};
</script>

<template>
  <div v-bind="$attrs" class="le-search-bar">
    <form role="search" @submit.prevent="onSearch(query)">
      <div
        class="le-search-bar__input-container"
        :class="{ 'le-search-bar__input-container--input-focused': isFocused }"
      >
        <template v-if="filterIsActive">
          <LeLanguagePair
            class="le-search-bar__language-pair"
            :aria-label="languagePairAria"
            :direction="searchFilterStore.filter.direction"
            :source-language="searchFilterStore.filter.sourceLanguage!"
            :target-language="searchFilterStore.filter.targetLanguage"
            @click="onLanguageSelectorButtonClick()"
          />

          <span class="le-search-bar__language-pair-divider"></span>
        </template>

        <LeTooltip
          v-if="showFreeSearchCountdown"
          :text="t('le-search-bar.free-searches-left')"
          :margin-bottom="10"
          class="le-search-bar__free-searches-left-tooltip"
        >
          <LeLabel :color="freeSearchLabelColor">
            {{ freeSearchesLeft }}
          </LeLabel>
        </LeTooltip>

        <LeKeyboards
          ref="keyboards"
          class="le-search-bar__keyboard-toggle-button"
          @key="onKeyboardKey($event)"
          @backspace="onKeyboardBackspace()"
          @close="setSearchBarFocus()"
        />

        <Combobox :model-value="query" @update:model-value="updateQuery($event)">
          <ComboboxButton as="div" class="le-search-bar__combobox-button">
            <ComboboxInput
              ref="comboboxInput"
              v-focus-on-type="focusOnTypeConfig"
              :autofocus="props.autofocus"
              class="le-search-bar__input"
              :aria-label="t('le-search-bar.search')"
              :placeholder="t('le-search-bar.search-placeholder')"
              autocomplete="off"
              autocorrect="off"
              spellcheck="false"
              autocapitalize="false"
              type="text"
              @focus="setFocus($event, true)"
              @blur="setFocus($event, false)"
              @keydown.enter.prevent="
                onSearch(query);
                trackEnterSearch();
              "
              @input="query = $event.target.value"
            />
          </ComboboxButton>

          <LeIconButton
            v-if="query && !props.isLoading"
            class="le-search-bar__clear-button"
            :aria-label="t('le-search-bar.clear')"
            @click="onClearQuery()"
          >
            <LeCross />
          </LeIconButton>

          <LeSpinner v-if="props.isLoading" aria-hidden="true" class="le-search-bar__spinner" />

          <span v-if="props.isLoading || query" class="le-search-bar__language-selector-divider"></span>

          <LeLanguageSelectorButton
            class="le-search-bar__language-selector-button"
            :aria-label="languageSelectorButtonAria"
            :filter="searchFilterStore.filter"
            @click.prevent="onLanguageSelectorButtonClick()"
          />

          <LeIconButton
            color="primary"
            variant="dark"
            size="xl"
            class="le-search-bar__submit-button"
            type="submit"
            :aria-label="t('le-search-bar.search')"
            @click="trackIconSearch()"
          >
            <LeMagnifyingGlass role="presentation" class="le-search-bar__submit-button-icon" />
          </LeIconButton>

          <Transition v-show="query && isSuggestionsOpen" name="le-search-bar__suggestions-transition">
            <ComboboxOptions v-if="props.suggestions.length > 0" class="le-search-bar__suggestions" static>
              <ComboboxOption style="display: none" aria-hidden="true" :value="query">
                {{ query }}
              </ComboboxOption>
              <ComboboxOption
                v-for="suggestion in props.suggestions"
                :key="suggestion.query"
                v-slot="{ active }"
                :value="suggestion"
                as="template"
              >
                <li
                  class="le-search-bar__suggestions-item"
                  :class="{
                    'le-search-bar__suggestions-item--active': active
                  }"
                  @click.prevent="
                    () => {
                      trackSuggestionClick();
                      updateQuery(suggestion);
                      onSearch(suggestion.query);
                    }
                  "
                >
                  <LeLabel v-if="isSmartComplete(suggestion)" class="le-search-bar__suggestions-item-label">{{
                    t("le-search-bar.labels.suggestion")
                  }}</LeLabel>
                  <VueDiff :text="query" :new-text="suggestion.query">
                    <template #insert="diff">
                      <ins class="le-search-bar__suggestions-item-diff">{{ diff.value }}</ins>
                    </template>
                    <template #replace="diff">
                      <ins class="le-search-bar__suggestions-item-diff">{{ diff.value }}</ins>
                    </template>
                  </VueDiff>
                </li>
              </ComboboxOption>
            </ComboboxOptions>
          </Transition>
        </Combobox>
      </div>
    </form>

    <LeLanguageSelector
      v-if="showLanguageSelector"
      class="le-search-bar__language-selector"
      :available-source-languages="searchFilterStore.availableSourceLanguages"
      :available-search-directions="searchFilterStore.availableSearchDirections"
      :available-target-languages="searchFilterStore.availableTargetLanguages"
      :source-language="searchFilterStore.filter.sourceLanguage"
      :direction="searchFilterStore.filter.direction"
      :target-language="searchFilterStore.filter.targetLanguage"
      @source-language-change="onSourceLanguageChange($event)"
      @direction-change="onDirectionChange($event)"
      @target-language-change="onTargetLanguageChange($event)"
    />
  </div>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";
@import "@/assets/styles/z-index.less";

.le-search-bar {
  position: relative;
  z-index: @le-search-bar__z-index;

  &__input-container {
    display: flex;
    height: 56px;
    padding: 0 12px;
    border: 1px solid @colors[@secondary][600];
    border-radius: 4px;
    background-color: white;
    place-items: center;

    #media-breakpoint-up(md, {
      height: 58px;
      padding: 0 24px;
    });

    &--input-focused {
      .focus-outline(green);
      border: 1px solid @colors[@primary][600];
    }
  }

  &__language-pair-divider,
  &__language-selector-divider {
    height: calc(100% - 25px);
    border-left: 1px solid @colors[@secondary][600];
  }

  &__language-pair-divider {
    display: none;

    #media-breakpoint-up(md, {
      display: inherit;
      margin: 0 12px;
    });
  }

  &__language-selector-divider {
    margin-left: 12px;
  }

  &__language-pair {
    display: none;

    #media-breakpoint-up(md, {
      display: flex;
      gap: 6px;
      justify-content: space-between;
      min-width: 62px;
      .body-text("s", "semi-bold");
      color: @colors[@secondary][800];
      cursor: pointer;
    });
  }

  &__free-searches-left-tooltip {
    display: none;

    #media-breakpoint-up(lg, {
      display: inherit;
      margin-right: 12px;
    });
  }

  &__keyboard-toggle-button {
    display: none;

    #media-breakpoint-up(md, {
      display: inherit;
      margin-right: 12px;
    });
  }

  &__combobox-button {
    display: flex;
    flex: 1 10 auto;
    height: 100%;
  }

  &__input {
    width: 100%;
    height: 100%;
    border: 0;
    color: @colors[@neutrals][500];
    font-size: 1rem;
    font-weight: 400;

    &::placeholder {
      opacity: 0.3;
      color: @colors[@neutrals][500];
    }
  }

  &__clear-button,
  &__spinner {
    margin-left: 12px;
  }

  &__language-selector-button {
    margin-left: 12px;
  }

  &__submit-button {
    display: flex;
    justify-content: center;
    margin-left: 12px;
    transition: all 75ms ease-out;

    &-icon {
      width: 22px;
      height: 22px;
    }
  }

  &__language-selector {
    margin-top: 8px;
  }

  &__suggestions {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    margin-top: 8px;
    padding: 4px;
    border-radius: 4px;
    background: white;
    box-shadow: @shadows[dropdown];
    list-style: none;
    text-align: left;

    #media-breakpoint-up(md, {
      padding: 8px;
    });

    &-item {
      padding: 8px 12px;
      border-radius: 4px;
      color: @colors[@neutrals][500];
      font-size: 1rem;
      line-height: 1.375rem;
      cursor: pointer;

      #media-breakpoint-up(md, {
        padding: 8px 16px;
      });

      &--active {
        background-color: @colors[@secondary][200];
      }

      &-label {
        margin-right: 8px;
      }

      &-diff {
        color: @colors[@primary][800];
        text-decoration: none;
      }
    }

    &-transition-enter-active {
      transition: all 100ms ease-out;
    }

    &-transition-enter-from {
      transform: translateY(-5px);
      opacity: 0;
    }
  }
}
</style>
